import { formatReportDate, formatReportDateRange, formatReportTimestamp, getReportStatusClass, getReportStatusIcon, getReportStatusLabel } from "../../shared/utils/report.utils";
import { Component, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { environment } from '../../../environments/environment';
import { FaaReportService, BatchMissingCountSummary } from '../../core/services/faa-report.service';
import {
  FaaReport,
  FaaReportWithJobs,
  FaaReportStatus,
  FaaReportOptions,
  QueueStats
} from '../../core/models/faa-report.model';

interface Organization {
  id: string;
  name: string;
}

interface ApiResponse<T> {
  success: boolean;
  data?: T;
}

/**
 * FAA Reports Admin Page
 *
 * Displays all FAA reports across organizations with filtering,
 * queue statistics, and management actions (view, delete, regenerate, retry).
 * Includes batch report generation functionality.
 */
@Component({
  selector: 'app-faa-reports',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './faa-reports.component.html',
  styleUrl: './faa-reports.component.scss'
})
export class FaaReportsComponent implements OnInit, OnDestroy {
  reports: FaaReport[] = [];
  organizations: Organization[] = [];
  queueStats: QueueStats = { pending: 0, active: 0, completed: 0, failed: 0 };
  loading = true;
  errorMessage = '';
  successMessage = '';

  // Polling for generating reports
  private pollingInterval: ReturnType<typeof setInterval> | null = null;
  private readonly POLLING_INTERVAL_MS = 3000; // Poll every 3 seconds when generating

  // Pagination
  currentPage = 1;
  pageSize = 20;
  totalReports = 0;
  totalPages = 0;

  // Filters
  searchQuery = '';
  filterStatus: 'all' | FaaReportStatus = 'all';
  filterOrganization = '';
  filterStartDate = '';
  filterEndDate = '';

  // Modal state
  selectedReport: FaaReportWithJobs | null = null;
  detailModalVisible = false;
  deleteModalVisible = false;
  regenerateModalVisible = false;
  reportToDelete: FaaReport | null = null;
  reportToRegenerate: FaaReport | null = null;
  actionLoading = false;

  // Date editing state for detail modal
  isEditingDates = false;
  editStartDate = '';
  editEndDate = '';
  dateEditError = '';

  // Status options for filter
  statusOptions: { value: string; label: string }[] = [
    { value: 'all', label: 'All Statuses' },
    { value: 'draft', label: 'Draft' },
    { value: 'generating', label: 'Generating' },
    { value: 'ready', label: 'Ready' },
    { value: 'submitted', label: 'Submitted' },
    { value: 'failed', label: 'Failed' }
  ];

  // ============================================
  // BATCH GENERATION STATE
  // ============================================

  batchSectionExpanded = false;
  selectedOrganizations: Set<string> = new Set();
  filteredOrganizations: Organization[] = [];
  batchOrgSearch = '';
  batchStartDate = '';
  batchEndDate = '';

  /** FAA §F.2.d pre-generation warning; null when unavailable (never blocks). */
  missingCounts: BatchMissingCountSummary | null = null;
  missingCountsLoading = false;
  /** Monotonic stamp so only the newest in-flight warning request applies. */
  private missingCountsRequestId = 0;
  batchOptions: FaaReportOptions = {
    includeFlightLogs: true,
    includeSprayRecords: true,
    includePilotInfo: true,
    includeAircraftInfo: true
  };
  batchConfirmModalVisible = false;
  batchGenerating = false;

  // Batch limit warning threshold
  private readonly BATCH_WARNING_THRESHOLD = 50;

  constructor(
    private faaReportService: FaaReportService,
    private http: HttpClient
  ) {}

  ngOnInit(): void {
    this.loadOrganizations();
    this.loadQueueStats();
    this.loadReports();
    this.initializeBatchDates();
  }

  ngOnDestroy(): void {
    this.stopPolling();
  }

  // ============================================
  // POLLING FOR GENERATING REPORTS
  // ============================================

  private startPolling(): void {
    if (this.pollingInterval) return; // Already polling

    this.pollingInterval = setInterval(async () => {
      // Silently refresh data without showing loading indicator
      try {
        const filters: any = {
          page: this.currentPage,
          pageSize: this.pageSize
        };

        if (this.filterStatus !== 'all') {
          filters.status = this.filterStatus;
        }
        if (this.filterOrganization) {
          filters.organizationId = this.filterOrganization;
        }
        if (this.filterStartDate) {
          filters.startDate = this.filterStartDate;
        }
        if (this.filterEndDate) {
          filters.endDate = this.filterEndDate;
        }
        if (this.searchQuery.trim()) {
          filters.search = this.searchQuery.trim();
        }

        const result = await this.faaReportService.listReports(filters);
        this.reports = result.reports;
        this.totalReports = result.total;
        this.totalPages = Math.ceil(this.totalReports / this.pageSize);

        // Also refresh queue stats
        this.queueStats = await this.faaReportService.getQueueStats();

        // Check if we should continue polling
        this.checkAndUpdatePolling();
      } catch (error) {
        console.error('Polling refresh error:', error);
      }
    }, this.POLLING_INTERVAL_MS);
  }

  private stopPolling(): void {
    if (this.pollingInterval) {
      clearInterval(this.pollingInterval);
      this.pollingInterval = null;
    }
  }

  private checkAndUpdatePolling(): void {
    const hasGenerating = this.reports.some(r => r.status === 'generating') ||
                          this.queueStats.active > 0 ||
                          this.queueStats.pending > 0;

    if (hasGenerating && !this.pollingInterval) {
      this.startPolling();
    } else if (!hasGenerating && this.pollingInterval) {
      this.stopPolling();
    }
  }

  // ============================================
  // DATA LOADING
  // ============================================

  async loadOrganizations(): Promise<void> {
    try {
      const response = await firstValueFrom(
        this.http.get<ApiResponse<{ organizations: Organization[]; total: number }>>(
          `${environment.apiUrl}/admin/organizations?pageSize=1000`
        )
      );

      if (response.success && response.data) {
        this.organizations = response.data.organizations;
        // Initialize filtered organizations for batch selection
        this.filteredOrganizations = [...this.organizations];
      }
    } catch (error: any) {
      console.error('Failed to load organizations:', error);
    }
  }

  async loadQueueStats(): Promise<void> {
    try {
      this.queueStats = await this.faaReportService.getQueueStats();
    } catch (error: any) {
      console.error('Failed to load queue stats:', error);
    }
  }

  async loadReports(): Promise<void> {
    this.loading = true;
    this.errorMessage = '';

    try {
      const filters: any = {
        page: this.currentPage,
        pageSize: this.pageSize
      };

      if (this.filterStatus !== 'all') {
        filters.status = this.filterStatus;
      }
      if (this.filterOrganization) {
        filters.organizationId = this.filterOrganization;
      }
      if (this.filterStartDate) {
        filters.startDate = this.filterStartDate;
      }
      if (this.filterEndDate) {
        filters.endDate = this.filterEndDate;
      }
      if (this.searchQuery.trim()) {
        filters.search = this.searchQuery.trim();
      }

      const result = await this.faaReportService.listReports(filters);
      this.reports = result.reports;
      this.totalReports = result.total;
      this.totalPages = Math.ceil(this.totalReports / this.pageSize);

      // Start/stop polling based on whether there are generating reports
      this.checkAndUpdatePolling();
    } catch (error: any) {
      console.error('Load reports error:', error);
      this.errorMessage = 'Failed to load reports';
    } finally {
      this.loading = false;
    }
  }

  // ============================================
  // FILTER HANDLERS
  // ============================================

  onSearch(): void {
    this.currentPage = 1;
    this.loadReports();
  }

  onFilterChange(): void {
    this.currentPage = 1;
    this.loadReports();
  }

  clearFilters(): void {
    this.searchQuery = '';
    this.filterStatus = 'all';
    this.filterOrganization = '';
    this.filterStartDate = '';
    this.filterEndDate = '';
    this.currentPage = 1;
    this.loadReports();
  }

  // ============================================
  // PAGINATION
  // ============================================

  goToPage(page: number): void {
    if (page >= 1 && page <= this.totalPages) {
      this.currentPage = page;
      this.loadReports();
    }
  }

  get paginationRange(): number[] {
    const range: number[] = [];
    const maxButtons = 5;
    let start = Math.max(1, this.currentPage - Math.floor(maxButtons / 2));
    let end = Math.min(this.totalPages, start + maxButtons - 1);

    if (end - start < maxButtons - 1) {
      start = Math.max(1, end - maxButtons + 1);
    }

    for (let i = start; i <= end; i++) {
      range.push(i);
    }

    return range;
  }

  // ============================================
  // VIEW REPORT DETAIL
  // ============================================

  async viewReport(report: FaaReport): Promise<void> {
    try {
      this.selectedReport = await this.faaReportService.getReport(report.id);
      this.detailModalVisible = true;
      this.openModal('detailModal');
    } catch (error: any) {
      console.error('Failed to load report details:', error);
      this.errorMessage = 'Failed to load report details';
    }
  }

  closeDetailModal(): void {
    this.detailModalVisible = false;
    this.selectedReport = null;
    this.closeModal('detailModal');
  }

  openReportUrl(): void {
    // Use downloadUrl (pre-signed S3 URL) if available, fall back to reportUrl
    const url = this.selectedReport?.downloadUrl || this.selectedReport?.reportUrl;
    if (url) {
      window.open(url, '_blank');
    }
  }

  // ============================================
  // DELETE ACTION
  // ============================================

  confirmDelete(report: FaaReport): void {
    this.reportToDelete = report;
    this.deleteModalVisible = true;
    this.openModal('deleteModal');
  }

  cancelDelete(): void {
    this.reportToDelete = null;
    this.deleteModalVisible = false;
    this.closeModal('deleteModal');
  }

  async executeDelete(): Promise<void> {
    if (!this.reportToDelete) return;

    this.actionLoading = true;
    this.errorMessage = '';

    try {
      const success = await this.faaReportService.deleteReport(this.reportToDelete.id);

      if (success) {
        this.successMessage = 'Report deleted successfully';
        this.cancelDelete();
        await this.loadReports();
        await this.loadQueueStats();
        this.clearSuccessMessage();
      } else {
        this.errorMessage = 'Failed to delete report';
      }
    } catch (error: any) {
      console.error('Delete error:', error);
      this.errorMessage = error?.error?.error || 'Failed to delete report';
    } finally {
      this.actionLoading = false;
    }
  }

  // ============================================
  // REGENERATE ACTION
  // ============================================

  confirmRegenerate(report: FaaReport): void {
    this.reportToRegenerate = report;
    this.regenerateModalVisible = true;
    this.openModal('regenerateModal');
  }

  cancelRegenerate(): void {
    this.reportToRegenerate = null;
    this.regenerateModalVisible = false;
    this.closeModal('regenerateModal');
  }

  async executeRegenerate(): Promise<void> {
    if (!this.reportToRegenerate) return;

    this.actionLoading = true;
    this.errorMessage = '';

    try {
      const success = await this.faaReportService.regenerateReport(this.reportToRegenerate.id);

      if (success) {
        this.successMessage = 'Report regeneration started';
        this.cancelRegenerate();
        await this.loadReports();
        await this.loadQueueStats();
        this.clearSuccessMessage();
      } else {
        this.errorMessage = 'Failed to regenerate report';
      }
    } catch (error: any) {
      console.error('Regenerate error:', error);
      this.errorMessage = error?.error?.error || 'Failed to regenerate report';
    } finally {
      this.actionLoading = false;
    }
  }

  // ============================================
  // RETRY ACTION
  // ============================================

  async retryReport(report: FaaReport): Promise<void> {
    if (report.status !== 'failed') return;

    this.actionLoading = true;
    this.errorMessage = '';

    try {
      const success = await this.faaReportService.retryReport(report.id);

      if (success) {
        this.successMessage = 'Report retry started';
        await this.loadReports();
        await this.loadQueueStats();
        this.clearSuccessMessage();
      } else {
        this.errorMessage = 'Failed to retry report';
      }
    } catch (error: any) {
      console.error('Retry error:', error);
      this.errorMessage = error?.error?.error || 'Failed to retry report';
    } finally {
      this.actionLoading = false;
    }
  }

  // ============================================
  // BATCH GENERATION METHODS
  // ============================================

  /**
   * Initialize batch date range to last 30 days
   */
  private initializeBatchDates(): void {
    const today = new Date();
    const thirtyDaysAgo = new Date(today);
    thirtyDaysAgo.setDate(today.getDate() - 30);

    this.batchEndDate = this.formatDateForInput(today);
    this.batchStartDate = this.formatDateForInput(thirtyDaysAgo);
  }

  /**
   * Format date for HTML date input (YYYY-MM-DD)
   */
  private formatDateForInput(date: Date): string {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  /**
   * Toggle batch generation section expansion
   */
  toggleBatchSection(): void {
    this.batchSectionExpanded = !this.batchSectionExpanded;
  }

  /**
   * Toggle organization selection for batch generation
   */
  toggleOrganizationSelection(orgId: string): void {
    if (this.selectedOrganizations.has(orgId)) {
      this.selectedOrganizations.delete(orgId);
    } else {
      this.selectedOrganizations.add(orgId);
    }
  }

  /**
   * Check if organization is selected
   */
  isOrganizationSelected(orgId: string): boolean {
    return this.selectedOrganizations.has(orgId);
  }

  /**
   * Select all filtered (visible) organizations
   */
  selectAllFiltered(): void {
    this.filteredOrganizations.forEach(org => {
      this.selectedOrganizations.add(org.id);
    });
  }

  /**
   * Clear all selected organizations
   */
  clearSelection(): void {
    this.selectedOrganizations.clear();
  }

  /**
   * Apply search filter to organization list for batch selection
   */
  applyBatchOrgFilter(): void {
    const searchLower = this.batchOrgSearch.toLowerCase().trim();
    if (!searchLower) {
      this.filteredOrganizations = [...this.organizations];
    } else {
      this.filteredOrganizations = this.organizations.filter(org =>
        org.name.toLowerCase().includes(searchLower)
      );
    }
  }

  /**
   * Validate batch date range
   */
  isDateRangeValid(): boolean {
    if (!this.batchStartDate || !this.batchEndDate) {
      return false;
    }
    const start = new Date(this.batchStartDate);
    const end = new Date(this.batchEndDate);
    return end >= start;
  }

  /**
   * Check if batch form is valid for submission
   */
  canSubmitBatch(): boolean {
    return this.selectedOrganizations.size > 0 && this.isDateRangeValid();
  }

  /**
   * Check if batch selection exceeds warning threshold
   */
  showBatchWarning(): boolean {
    return this.selectedOrganizations.size > this.BATCH_WARNING_THRESHOLD;
  }

  /**
   * Get batch confirmation text for modal
   */
  getBatchConfirmationText(): string {
    const count = this.selectedOrganizations.size;
    const orgWord = count === 1 ? 'organization' : 'organizations';
    return `Generate ${count} FAA report${count === 1 ? '' : 's'} for ${count} ${orgWord}?`;
  }


  /**
   * Show batch generation confirmation modal
   */
  confirmBatchGeneration(): void {
    if (!this.canSubmitBatch()) return;
    this.batchConfirmModalVisible = true;
    this.openModal('batchConfirmModal');

    // FAA §F.2.d — fetch the missing-count warning alongside opening the modal
    // rather than awaiting it first, so a slow or unavailable check never
    // delays the confirm dialog. The modal renders the warning when it lands.
    this.loadMissingFlightCounts();
  }

  /**
   * Records across the selected orgs with no flight count entered, which the
   * filing will state as 1 each.
   *
   * Advisory. `null` means "not available" and the modal simply shows nothing
   * extra — an operator must never be blocked from filing by a warning that
   * failed to compute.
   */
  private async loadMissingFlightCounts(): Promise<void> {
    this.missingCounts = null;
    this.missingCountsLoading = true;

    // The org selection and date range stay editable while this is in flight,
    // so a slow earlier request can resolve after a faster later one and
    // overwrite it with a stale warning. Stamp each request and let only the
    // newest apply — a warning that describes a filing the operator is no
    // longer about to generate is worse than no warning at all.
    const requestId = ++this.missingCountsRequestId;
    const organizationIds = Array.from(this.selectedOrganizations);

    try {
      const result = await this.faaReportService.fetchMissingFlightCounts(
        organizationIds,
        this.batchStartDate,
        this.batchEndDate
      );
      if (requestId !== this.missingCountsRequestId) {
        return;
      }
      this.missingCounts = result;
    } finally {
      // Only the newest request owns the loading flag, or a superseded one
      // clearing it would hide the spinner while a live request is still out.
      if (requestId === this.missingCountsRequestId) {
        this.missingCountsLoading = false;
      }
    }
  }

  /**
   * Cancel batch generation confirmation
   */
  cancelBatchConfirmation(): void {
    this.batchConfirmModalVisible = false;
    this.closeModal('batchConfirmModal');
  }

  /**
   * Execute batch generation after confirmation
   */
  async executeBatchGeneration(): Promise<void> {
    if (!this.canSubmitBatch()) return;

    this.batchGenerating = true;
    this.errorMessage = '';

    try {
      const organizationIds = Array.from(this.selectedOrganizations);

      const result = await this.faaReportService.generateBatch(
        organizationIds,
        this.batchStartDate,
        this.batchEndDate,
        this.batchOptions
      );

      if (result.created > 0) {
        this.successMessage = `Successfully queued ${result.created} FAA report${result.created === 1 ? '' : 's'} for generation`;

        // Report any errors
        if (result.errors && result.errors.length > 0) {
          const errorCount = result.errors.length;
          this.successMessage += `. ${errorCount} organization${errorCount === 1 ? '' : 's'} had errors.`;
        }

        // Reset batch form state
        this.clearSelection();
        this.batchSectionExpanded = false;
        this.cancelBatchConfirmation();

        // Refresh reports list and queue stats
        await this.loadReports();
        await this.loadQueueStats();

        this.clearSuccessMessage();
      } else if (result.errors && result.errors.length > 0) {
        this.errorMessage = `Failed to generate reports: ${result.errors[0].error}`;
      } else {
        this.errorMessage = 'Failed to generate batch reports';
      }
    } catch (error: any) {
      console.error('Batch generation error:', error);
      this.errorMessage = error?.error?.error || 'Failed to generate batch reports';
    } finally {
      this.batchGenerating = false;
    }
  }

  // ============================================
  // MODAL HELPERS
  // ============================================

  private openModal(modalId: string): void {
    const modalElement = document.getElementById(modalId);
    if (modalElement) {
      const modal = new (window as any).bootstrap.Modal(modalElement);
      modal.show();
    }
  }

  private closeModal(modalId: string): void {
    const modalElement = document.getElementById(modalId);
    if (modalElement) {
      const modal = (window as any).bootstrap.Modal.getInstance(modalElement);
      if (modal) {
        modal.hide();
      }
    }
  }

  private clearSuccessMessage(): void {
    setTimeout(() => {
      this.successMessage = '';
    }, 3000);
  }

  // ============================================
  // FORMATTING HELPERS
  // ============================================

  formatDate(dateString: string | null | undefined): string {
    return formatReportDate(dateString);
  }

  formatDateRange(startDate: string, endDate: string): string {
    return formatReportDateRange(startDate, endDate);
  }

  formatTimestamp(timestamp: number | undefined): string {
    return formatReportTimestamp(timestamp);
  }

  getStatusClass(status: FaaReportStatus): string {
    return getReportStatusClass(status);
  }

  getStatusLabel(status: FaaReportStatus): string {
    return getReportStatusLabel(status);
  }

  getStatusIcon(status: FaaReportStatus): string {
    return getReportStatusIcon(status);
  }

  getOrganizationName(organizationId: string): string {
    const org = this.organizations.find(o => o.id === organizationId);
    return org?.name || organizationId;
  }

  canDelete(report: FaaReport): boolean {
    return report.status !== 'generating';
  }

  canRetry(report: FaaReport): boolean {
    return report.status === 'failed';
  }

  readonly Math = Math;
}
